import React, { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import COUNTRIES from "@/lib/countries.json";

export const flagOf = (code) => (code ? String.fromCodePoint(...[...code].map((c) => 127397 + c.charCodeAt(0))) : "");
export const COUNTRY_OPTIONS = COUNTRIES.map((c) => ({ value: c.name, label: c.name, icon: flagOf(c.code), search: c.code }));
export const NATIONALITY_OPTIONS = [...COUNTRIES].sort((a, b) => a.nationality.localeCompare(b.nationality))
  .map((c) => ({ value: c.nationality, label: c.nationality, icon: flagOf(c.code), search: c.name }));
const DIAL_OPTIONS = COUNTRIES.map((c) => ({ value: c.code, label: `${c.name} (${c.dial})`, icon: flagOf(c.code), search: c.dial }));

export const GENDERS = ["Male", "Female", "Other", "Prefer not to say"];
export const MARITAL = ["Single", "Married", "Divorced", "Widowed", "Prefer not to say"];
export const EDUCATION = ["No formal education", "Primary", "Secondary (10th)", "Higher Secondary (12th)", "ITI / Trade Certificate", "Diploma", "Bachelor's Degree", "Master's Degree", "Doctorate"];
export const EMPLOYMENT = ["Employed", "Unemployed", "Self-employed", "Working abroad", "Student"];
export const todayISO = () => new Date().toISOString().slice(0, 10);
// Normalise legacy dates ("1997/11/11", "11/11/1997") for <input type=date>.
export function toInputDate(v) {
  const s = String(v || "").trim();
  let m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}`;
  m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  return "";
}

// Legacy values like "+91 98765..." are split into code + number.
export function splitPhone(number, code) {
  const raw = String(number || "").trim();
  if (code || !raw.startsWith("+")) return { code: code || "", number: raw.replace(/[^\d]/g, "") };
  const compact = raw.replace(/[\s-]/g, "");
  const dial = COUNTRIES.map((c) => c.dial).sort((a, b) => b.length - a.length).find((d) => compact.startsWith(d));
  return dial ? { code: dial, number: compact.slice(dial.length) } : { code: "", number: compact.replace(/[^\d]/g, "") };
}
export const isValidPhone = (n) => !n || /^\d{6,14}$/.test(n);

/* Searchable dropdown (combobox). value is the stored string. */
export function SearchSelect({ value, onChange, options, placeholder = "Select", testId, className = "", display }) {
  const [open, setOpen] = useState(false);
  const cur = options.find((o) => o.value === value);
  const shown = cur ? (display ? display(cur) : `${cur.icon ? `${cur.icon} ` : ""}${cur.label}`) : value;
  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} role="combobox" aria-expanded={open}
          className={`w-full h-10 px-3 rounded-md border border-input bg-background text-sm flex items-center justify-between gap-2 text-left focus:outline-none focus:ring-2 focus:ring-ring ${className}`}>
          <span className={`truncate ${shown ? "" : "text-muted-foreground"}`}>{shown || placeholder}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[240px]" align="start">
        <Command>
          <CommandInput data-testid={testId ? `${testId}-search` : undefined} placeholder="Search..." />
          <CommandList className="max-h-64">
            <CommandEmpty>No matches found.</CommandEmpty>
            {options.map((o) => (
              <CommandItem key={o.key || o.value} value={`${o.label} ${o.search || ""}`} data-testid={testId ? `${testId}-opt-${o.key || o.value}` : undefined}
                onSelect={() => { onChange(o.value); setOpen(false); }}>
                <Check className={`mr-2 h-4 w-4 ${value === o.value ? "opacity-100" : "opacity-0"}`} />
                {o.icon ? <span className="mr-2">{o.icon}</span> : null}{o.label}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export const CountrySelect = (p) => <SearchSelect {...p} options={COUNTRY_OPTIONS} placeholder={p.placeholder || "Select country"} />;
export const NationalitySelect = (p) => <SearchSelect {...p} options={NATIONALITY_OPTIONS} placeholder={p.placeholder || "Select nationality"} />;

/* Plain dropdown for short fixed lists. */
export function OptionSelect({ value, onChange, options, testId }) {
  return (
    <select data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
      <option value="">Select</option>
      {value && !options.includes(value) ? <option value={value}>{value}</option> : null}
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

/* Calling-code picker + digits-only number. value = { code, number } */
export function PhoneInput({ value, onChange, testId }) {
  const v = value || { code: "", number: "" };
  const [iso, setIso] = useState(() => v.iso || COUNTRIES.find((c) => c.dial === v.code)?.code || "");
  const pick = v.iso || iso;
  const isoNow = COUNTRIES.find((c) => c.code === pick)?.dial === v.code ? pick : COUNTRIES.find((c) => c.dial === v.code)?.code || "";
  const bad = !isValidPhone(v.number);
  return (
    <div>
      <div className="flex gap-2">
        <SearchSelect testId={testId ? `${testId}-code` : undefined} value={isoNow} options={DIAL_OPTIONS} placeholder={v.code || "Code"} className="!w-32 shrink-0"
          display={(o) => `${o.icon} ${COUNTRIES.find((c) => c.code === o.value).dial}`}
          onChange={(code) => { setIso(code); onChange({ ...v, iso: code, code: COUNTRIES.find((c) => c.code === code).dial }); }} />
        <Input data-testid={testId} inputMode="tel" placeholder="Phone number" value={v.number} className={bad ? "border-red-400" : ""}
          onChange={(e) => onChange({ ...v, number: e.target.value.replace(/[^\d]/g, "").slice(0, 14) })} />
      </div>
      {bad ? <p className="text-xs text-red-500 mt-1">Enter 6 to 14 digits.</p> : null}
    </div>
  );
}
